import first from 'lodash/fp/first';
import isEmpty from 'lodash/fp/isEmpty';
import isNil from 'lodash/fp/isNil';
import isUndefined from 'lodash/fp/isUndefined';
import last from 'lodash/fp/last';
import subtract from 'lodash/fp/subtract';
import { v4 as uuid } from 'uuid';
import { Unit, WINDOW_BOUNDING_BOX_DEGREES } from '../../common/constants';
import type { Coords } from '../../common/types';
import {
    convertMetersIntoKilometers,
    convertSecondsIntoHHmmDisplay,
    coordinatesToCoords,
    getIntlFormattedCurrency,
} from '../../common/utils';
import type { RootDispatch, RootState } from '../../configuration/setup/store';
import type { AssetDataResponse, Coordinates, Currency } from '../../store/facade/facadeApiV3';
import { ItinerarySectionType, TimelineEventType } from '../../store/facade/facadeApiV3';
import { selectVehicleCosts } from '../../store/route/routeSelectors';
import { selectOrderedWaypoints } from '../../store/search/searchSelectors';
import type { Waypoint } from '../../store/search/searchSlice';
import type { Itinerary } from '../../store/tour/tourSlice';
import { selectBatteryCapacity, selectVehicleProfile } from '../../store/vehicleProfile/vehicleProfileSelectors';
import { renderShapes } from '../../store/widget/actions/renderShapes';
import { sendMessage } from '../../store/widget/widgetMessaging';
import { widgetActions } from '../../store/widget/widgetSlice';

export const BATTERY_TOP_THRESHOLD = 20;
export const BATTERY_BOTTOM_THRESHOLD = 0;

export interface MarkerProps {
    active: boolean;
    iconNames: string[];
    markerColor: string;
    name?: string;
    clickable?: boolean;
    fixed?: boolean;
}

export interface Marker {
    id: string;
    position: Coords;
    markerProps: MarkerProps;
    data?: Record<string, unknown>;
}

export interface SegmentCustomData {
    data: {
        routeId: string;
        routeType: 'selected' | 'alternative';
    };
}

export interface Segment {
    points: Coords[];
    alternative: boolean;
    reduced?: boolean;
    showArrows?: boolean;
    style?: {
        color: string;
        borderColor: string;
    };
    middleIconProps?: MarkerProps;
    customData?: SegmentCustomData;
    data?: Record<string, unknown>;
}

export interface AssetLockedInfo extends AssetDataResponse {
    batteryMaxPower?: number;
}

export enum RouteColor {
    Success = 0,
    Warning = 1,
    Danger = 2,
}

const getDistributedMarkerPosition = (
    coordinates: Coordinates[],
    numberOfRoutes: number,
    iteration: number
): Coords => {
    const divisor = numberOfRoutes + 1;
    const step = iteration + 1;
    return coordinatesToCoords(coordinates[Math.floor((coordinates.length / divisor) * step)]);
};

const createRouteInfoMarker = (coordinates: Coords, isActive: boolean, info: string): Marker => {
    return {
        id: uuid(),
        position: coordinates,
        markerProps: {
            active: isActive,
            name: info,
            iconNames: ['route'],
            markerColor: 'bg-map-marker-route',
            clickable: false,
            fixed: true,
        },
    };
};

const createRouteWarningMarker = (): MarkerProps => {
    return {
        active: false,
        iconNames: ['battery-level-low'],
        markerColor: 'bg-map-marker-warning',
        clickable: false,
        fixed: true,
    };
};

const createRouteDangerMarker = (): MarkerProps => {
    return {
        active: false,
        iconNames: ['battery-level-empty'],
        markerColor: 'bg-map-marker-danger',
        clickable: false,
        fixed: true,
    };
};

const createWaypointMarker = (position: Coords, icon: string, stationId?: string): Marker => ({
    id: uuid(),
    position,
    markerProps: {
        active: false,
        iconNames: [icon],
        markerColor: 'bg-map-marker-route',
        clickable: !!stationId,
    },
    ...(stationId ? { data: { stationId } } : {}),
});

export const createRouteInfo = ({ summary }: Itinerary, _currency: Currency): string => {
    const routeDuration = convertSecondsIntoHHmmDisplay(summary?.duration);
    const routeDistance = convertMetersIntoKilometers(summary?.distance)?.toString();
    const itinerarySummary = [];
    if (!isUndefined(routeDuration)) {
        itinerarySummary.push(routeDuration);
    }

    if (!isUndefined(routeDistance)) {
        itinerarySummary.push(` | ${routeDistance}${Unit.km}`);
    }

    if (!isUndefined(summary?.maxTotalCost) && !isUndefined(summary?.minTotalCost)) {
        const midValue = (summary.maxTotalCost + summary.minTotalCost) / 2;
        itinerarySummary.push(` | \u00B1${getIntlFormattedCurrency(midValue)}`);
    }

    return itinerarySummary.join('');
};

const addCoordinatesToSegment = (
    coordinates: Coordinates[],
    previousColor: RouteColor,
    segments: { success: Coordinates[]; warning: Coordinates[]; danger: Coordinates[] }
) => {
    switch (previousColor) {
        case RouteColor.Danger:
            segments.danger = segments.danger.concat(coordinates);
            break;
        case RouteColor.Warning:
            segments.warning = segments.warning.concat(coordinates);
            break;
        default:
            segments.success = segments.success.concat(coordinates);
            break;
    }
};

const determineRouteColor = (
    spanConsumption: number,
    batteryTopThreshold: number,
    batteryBottomThreshold: number
): RouteColor => {
    if (spanConsumption > batteryTopThreshold) {
        return RouteColor.Success;
    }
    if (spanConsumption > batteryBottomThreshold) {
        return RouteColor.Warning;
    }
    return RouteColor.Danger;
};

const createSegmentFromCoordinates = (
    coordinates: Coordinates[],
    itineraries: Itinerary[],
    selectedItinerary: number,
    isSelected: boolean,
    style?: { color: string; borderColor: string },
    middleIconProps?: MarkerProps,
    customData?: SegmentCustomData
): Segment => ({
    points: coordinates.map(coordinatesToCoords),
    alternative: itineraries.length > 1 && !isSelected,
    reduced: itineraries.length > 1 && !isSelected,
    showArrows: !(itineraries.length > 1 && !isSelected),
    style,
    middleIconProps,
    customData,
    data: {
        itineraryId: itineraries[selectedItinerary].itineraryId,
    },
});

const createTripSegments = (
    itineraries: Itinerary[],
    selectedItinerary: number,
    assetLockedInfo?: AssetLockedInfo
): Segment[] => {
    const reorderedItineraries = [
        ...itineraries.filter((_, index) => index !== selectedItinerary),
        itineraries[selectedItinerary],
    ];

    const buildCustomData = (isSelected: boolean, itineraryId: string): SegmentCustomData => ({
        data: {
            routeId: itineraryId,
            routeType: isSelected ? 'selected' : 'alternative',
        },
    });

    if (isNil(assetLockedInfo?.asset_id) || isNil(assetLockedInfo?.batteryMaxPower)) {
        return reorderedItineraries.map((itinerary, index, collection) => {
            const isSelected = collection.length - 1 === index;
            return createSegmentFromCoordinates(
                itinerary.drivingCoordinates,
                itineraries,
                selectedItinerary,
                isSelected,
                undefined,
                undefined,
                buildCustomData(isSelected, itinerary.itineraryId ?? '')
            );
        });
    }

    return reorderedItineraries.flatMap((it, index, collection) => {
        const isSelected = collection.length - 1 === index;
        const customData = buildCustomData(isSelected, it.itineraryId ?? '');
        const segments = {
            success: [] as Coordinates[],
            warning: [] as Coordinates[],
            danger: [] as Coordinates[],
        };
        const tripSegments: Segment[] = [];

        const { batteryMaxPower, state } = assetLockedInfo;
        const startingLevel = state?.energy_level ?? batteryMaxPower;

        if (isNil(batteryMaxPower) || isNil(startingLevel)) {
            return [
                createSegmentFromCoordinates(
                    it.drivingCoordinates,
                    itineraries,
                    selectedItinerary,
                    isSelected,
                    undefined,
                    undefined,
                    customData
                ),
            ];
        }

        const batteryTopThreshold = (BATTERY_TOP_THRESHOLD * batteryMaxPower) / 100;
        const batteryBottomThreshold = (BATTERY_BOTTOM_THRESHOLD * batteryMaxPower) / 100;
        let currConsumption = startingLevel;
        let previousColor: RouteColor = RouteColor.Success;

        it.sections?.forEach(section => {
            currConsumption = section.evBatteryCapacityDeparture ?? currConsumption;
            section.spans?.forEach(span => {
                if (isNil(span.coordinates)) {
                    return;
                }

                if (section.type === ItinerarySectionType.Transit) {
                    addCoordinatesToSegment(span.coordinates, previousColor, segments);
                    return;
                }

                if (isNil(span.ev_consumption)) {
                    addCoordinatesToSegment(span.coordinates, previousColor, segments);
                    return;
                }

                const spanConsumption = subtract(currConsumption, span.ev_consumption);
                const routeColor = determineRouteColor(spanConsumption, batteryTopThreshold, batteryBottomThreshold);

                addCoordinatesToSegment(span.coordinates, routeColor, segments);
                previousColor = routeColor;
                currConsumption = spanConsumption;
            });
        });

        // If no sections exist, use driving coordinates as fallback
        if (!it.sections?.length && it.drivingCoordinates?.length) {
            addCoordinatesToSegment(it.drivingCoordinates, RouteColor.Success, segments);
        }

        if (!isEmpty(segments.success)) {
            tripSegments.push(
                createSegmentFromCoordinates(
                    segments.success,
                    itineraries,
                    selectedItinerary,
                    isSelected,
                    undefined,
                    undefined,
                    customData
                )
            );
        }

        if (!isEmpty(segments.warning)) {
            tripSegments.push(
                createSegmentFromCoordinates(
                    segments.warning,
                    itineraries,
                    selectedItinerary,
                    isSelected,
                    { color: '#ff8e3c', borderColor: '#a75319' },
                    isSelected ? createRouteWarningMarker() : undefined,
                    customData
                )
            );
        }

        if (!isEmpty(segments.danger)) {
            tripSegments.push(
                createSegmentFromCoordinates(
                    segments.danger,
                    itineraries,
                    selectedItinerary,
                    isSelected,
                    { color: '#e22837', borderColor: '#500508' },
                    isSelected ? createRouteDangerMarker() : undefined,
                    customData
                )
            );
        }

        // Ensure we always return at least one segment
        if (tripSegments.length === 0) {
            return [
                createSegmentFromCoordinates(
                    it.drivingCoordinates,
                    itineraries,
                    selectedItinerary,
                    isSelected,
                    undefined,
                    undefined,
                    customData
                ),
            ];
        }

        return tripSegments;
    });
};

const createTripMarkers = (
    itineraries: Itinerary[],
    selectedItinerary: number,
    waypoints: Waypoint[],
    currency: Currency
): Marker[] => {
    const firstCoords = first(waypoints)?.coordinates;
    const lastCoords = last(waypoints)?.coordinates;
    const currentItinerary = itineraries.at(selectedItinerary);

    if (!firstCoords || !lastCoords || !currentItinerary) {
        return [];
    }

    const firstMarker = createWaypointMarker(firstCoords, 'start');
    const lastMarker = createWaypointMarker(lastCoords, 'finish');

    const intermediaryMarkers =
        currentItinerary.events?.timelineEvents
            ?.map(event => {
                if (event.timelineEventType === TimelineEventType.ChargeStop && event.coordinates) {
                    return createWaypointMarker(
                        coordinatesToCoords(event.coordinates),
                        'filling-e-station',
                        event.stationId
                    );
                }
                if (event.timelineEventType === TimelineEventType.IntermediateStop && event.coordinates) {
                    return createWaypointMarker(coordinatesToCoords(event.coordinates), 'arrow-down');
                }
                if (event.timelineEventType === TimelineEventType.Transit) {
                    const currSection = currentItinerary.sections?.find(
                        section => section.sectionId === event.sectionId
                    );
                    const transitCoords =
                        currSection?.spans
                            ?.flatMap(span => span.coordinates)
                            .filter(spanCoord => spanCoord !== undefined) ?? [];
                    const middleCoord = transitCoords[Math.floor(transitCoords.length / 2)];
                    return createWaypointMarker(coordinatesToCoords(middleCoord), 'transfer');
                }
                return undefined;
            })
            .filter(it => it !== undefined) ?? [];

    const routeInfoMarkers = itineraries.map((itinerary, index) => {
        const markerInfo = createRouteInfoMarker(
            getDistributedMarkerPosition(itinerary.drivingCoordinates, itineraries.length, index),
            index === selectedItinerary,
            createRouteInfo(itinerary, currency)
        );

        return markerInfo;
    });

    return [firstMarker, lastMarker, ...intermediaryMarkers, ...routeInfoMarkers];
};

export const propagateRoute =
    (itineraries: Itinerary[], selectedItinerary: number) => (dispatch: RootDispatch, _getState: () => RootState) => {
        return dispatch(propagateRoutes([itineraries[selectedItinerary]], 0));
    };

export const propagateRoutes =
    (itineraries: Itinerary[], selectedItinerary: number) => (_dispatch: RootDispatch, getState: () => RootState) => {
        if (itineraries.length < 1) {
            return;
        }

        const state = getState();
        const waypoints = selectOrderedWaypoints(state);
        const assetLocked = selectVehicleProfile(state).assetLocked;
        const batteryCapacity = selectBatteryCapacity(state).value;
        const assetLockedInfo: AssetLockedInfo | undefined = assetLocked
            ? { ...assetLocked, batteryMaxPower: batteryCapacity ?? 0 }
            : undefined;
        const { currency } = selectVehicleCosts(state);

        const route = {
            segments: createTripSegments(itineraries, selectedItinerary, assetLockedInfo),
            markers: createTripMarkers(itineraries, selectedItinerary, waypoints, currency as Currency),
        };

        sendMessage(widgetActions.renderRoute(route));
    };

export const requestBoundingBox = (itineraries: Itinerary[]) => {
    let minLat = Number.MAX_SAFE_INTEGER;
    let maxLat = Number.MIN_SAFE_INTEGER;
    let minLong = Number.MAX_SAFE_INTEGER;
    let maxLong = Number.MIN_SAFE_INTEGER;

    itineraries.forEach(itinerary => {
        itinerary.drivingCoordinates.map(coordinatesToCoords).forEach(coords => {
            if (coords.lat < minLat) {
                minLat = coords.lat;
            } else if (coords.lat > maxLat) {
                maxLat = coords.lat;
            }

            if (coords.lng < minLong) {
                minLong = coords.lng;
            } else if (coords.lng > maxLong) {
                maxLong = coords.lng;
            }
        });
    });

    sendMessage(
        widgetActions.setBoundingBox({
            bbox: {
                topLeft: {
                    lat: maxLat + WINDOW_BOUNDING_BOX_DEGREES,
                    lng: minLong - WINDOW_BOUNDING_BOX_DEGREES,
                },
                bottomRight: {
                    lat: minLat - WINDOW_BOUNDING_BOX_DEGREES,
                    lng: maxLong + WINDOW_BOUNDING_BOX_DEGREES,
                },
            },
        })
    );
};

export const clearRouteAndMarkers = () => {
    const route = {
        segments: [],
        markers: [],
    };

    sendMessage(widgetActions.renderRoute(route));
    renderShapes();
};
